import React from "react";
import Paper from "@mui/material/Paper";
import Table from "../mui/Table";
import TableBody from "@mui/material/TableBody";
import TableCell from "@mui/material/TableCell";
import TableContainer from "@mui/material/TableContainer";
import TableHead from "@mui/material/TableHead";
import { styled, CircularProgress, TableSortLabel } from "@mui/material";
import Pagination from "../mui/Pagination";
import TableRow from "@mui/material/TableRow";
import Box from "../mui/Box";
type Order = 'asc' | 'desc';
export interface Column {
  headerText: string;
  field: string;
  minWidth?: number;
  align?: "right" | "left" | "center";
  format?: (value: any) => string;
}

export interface Header {
  id: string;
  label: string;
  colSpan?: number;
  rowSpan?: number;
  minWidth?: string;
  align?: "left" | "right" | "center";
}

export interface GridTableProps {
  columns: Column[];
  rows: Record<string, any>[];
  headers?: Header[];
  rowsPerPageOptions?: number[];
  initialRowsPerPage?: number;
  tableHeight?: number;
  count?: number;
  handlePageChange?: (event: React.ChangeEvent<unknown>, page: number) => void;
  currentPage?: number;
  loading?: boolean; // New prop to indicate loading state
}

const GridTable: React.FC<GridTableProps> = ({
  columns,
  rows,
  headers,
  count = 0,
  rowsPerPageOptions = [10, 25, 100],
  initialRowsPerPage = 10,
  tableHeight = 440,
  handlePageChange,
  currentPage = 1,
  loading = false,
}) => {
  const [page, setPage] = React.useState(0);
  const [rowsPerPage, setRowsPerPage] = React.useState(initialRowsPerPage);
  const [order, setOrder] = React.useState<Order>('asc');
  const [orderBy, setOrderBy] = React.useState<string>('');

  const handleRequestSort = (property: string) => {
    const isAsc = orderBy === property && order === 'asc';
    setOrder(isAsc ? 'desc' : 'asc');
    setOrderBy(property);
  };
  const sortedRows = [...rows].sort((a, b) => {
    if (!orderBy) return 0; // No sorting if no column is selected
  
    let aValue = a[orderBy] ?? "";
    let bValue = b[orderBy] ?? "";
  
    // Function to check if a value is a valid date
    const parseDate = (value: any) => {
      if (typeof value !== "string") return NaN;
      const parts = value.split("/");
      if (parts.length === 3) {
        // Assuming format is DD/MM/YYYY
        const [day, month, year] = parts.map(Number);
        return new Date(year, month - 1, day).getTime(); // Convert to timestamp
      }
      return Date.parse(value); // Fallback for other formats
    };
  
    const aDate = parseDate(aValue);
    const bDate = parseDate(bValue);
  
    if (!isNaN(aDate) && !isNaN(bDate)) {
      return order === "asc" ? aDate - bDate : bDate - aDate;
    }
  
    // Handle numbers correctly
    if (!isNaN(aValue) && !isNaN(bValue)) {
      return order === "asc" ? aValue - bValue : bValue - aValue;
    }
  
    // Ensure values are strings for comparison
    aValue = String(aValue).toLowerCase();
    bValue = String(bValue).toLowerCase();
  
    if (aValue < bValue) return order === "asc" ? -1 : 1;
    if (aValue > bValue) return order === "asc" ? 1 : -1;
    return 0;
  });
  
  
  
  const handleChangeRowsPerPage = (
    event: React.ChangeEvent<HTMLInputElement>
  ) => {
    setRowsPerPage(+event.target.value);
    setPage(0);
  };

  const headerCellStyle: React.CSSProperties = {
    backgroundColor: "#DADADA",
    fontSize: "14px",
    border: "1px solid #e0e0e0",
    textAlign: "center",
    fontWeight: "bold",
  };

  const cellStyle: React.CSSProperties = {
    border: "1px solid #e0e0e0",
  };

  const zebraStyle = (index: number) => ({
    backgroundColor: "white",
  });

  return (
    <Paper sx={{ width: "100%", mt: 2, borderRadius: 2 }}>
      <TableContainer sx={{ borderRadius: 2, minHeight: tableHeight }}>
        {loading ? (
          <LoaderBox>
            <CircularProgress />
          </LoaderBox>
        ) : (
          <Table stickyHeader aria-label="attendance table">
            <TableHead>
              {headers && (
                <TableRow>
                  {headers.map((header, index) => (
                    <TableCell
                      key={index}
                      align={header.align || "center"}
                      colSpan={header.colSpan || 1}
                      rowSpan={header.rowSpan || 1}
                      style={{ ...headerCellStyle, minWidth: header.minWidth }}
                    >
                      <TableSortLabel
                        active={orderBy === header.id}
                        direction={orderBy === header.id ? order : "asc"}
                        onClick={() => handleRequestSort(header.id)}
                      >
                        {header.label}
                      </TableSortLabel>
                    </TableCell>
                  ))}
                </TableRow>
              )}
              <TableRow>
                {columns
                  .filter((column) => column.headerText)
                  .map((column) => (
                    <TableCell
                      key={column.field}
                      align={column.align}
                      style={{ ...headerCellStyle, minWidth: column.minWidth }}
                    >
                      <TableSortLabel
                        active={orderBy === column.field}
                        direction={orderBy === column.field ? order : "asc"}
                        onClick={() => handleRequestSort(column.field)}
                      >
                        {column.headerText}
                      </TableSortLabel>
                    </TableCell>
                  ))}
              </TableRow>
            </TableHead>
            <TableBody>
              {sortedRows
                .slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
                .map((row, rowIndex) => (
                  <TableRow
                    hover
                    role="checkbox"
                    tabIndex={-1}
                    key={rowIndex}
                    style={zebraStyle(rowIndex)}
                  >
                    {columns.map((column) => {
                      const value = row[column.field];
                      return (
                        <TableCell
                          key={column.field}
                          align={column.align}
                          style={cellStyle}
                        >
                          {column.format && typeof value === "number"
                            ? column.format(value)
                            : value || "--"}
                        </TableCell>
                      );
                    })}
                  </TableRow>
                ))}
            </TableBody>
          </Table>
        )}
      </TableContainer>
      {!loading && count > 0 && (
        <StyledBox>
          <Pagination
            sx={{ m: "auto" }}
            count={count}
            page={currentPage}
            onChange={handlePageChange}
          />
        </StyledBox>
      )}
    </Paper>
  );
};

export default GridTable;

const StyledBox = styled(Box)(({ theme }) => ({
  display: "flex",
  backgroundColor: "#ffffff",
  justifyContent: "center",
  borderTop: "1px solid #ccc",
  alignItems: "center",
  width: "100%",
  height: "50px",
}));

const LoaderBox = styled(Box)(({ theme }) => ({
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  height: "100%",
  width: "100%",
  marginTop:"200px"
}));
